import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { ScreenshotFrame } from "@/components/marketing/ScreenshotFrame";
import {
  Rocket, LayoutList, PenSquare, Library, Sparkles, Boxes, Waypoints, Download, ArrowRight, ListChecks,
  BookMarked, ShieldCheck, ExternalLink, Headphones,
} from "lucide-react";

import { AUDIOBOOKS_ENABLED } from "@/config/features";

const SECTIONS = [
  {
    id: "getting-started",
    icon: Rocket,
    title: "1. Getting started",
    intro: "Every story begins on your writing desk — the dashboard where all your books live.",
    steps: [
      "Create a free account with your email, and add a pen name if you'd like a public-facing identity.",
      "Click New project and give your book a title, genre and a one-line synopsis.",
      "Already have a draft? Use Import to drop in a .docx, .txt or .pdf — it's split into chapters and its details are extracted for you.",
    ],
  },
  {
    id: "blueprint",
    icon: LayoutList,
    title: "2. Plan with the Blueprint",
    intro: "The Blueprint is your bird's-eye view: a logline at the top, acts across the board, and chapter cards you can shape before you draft.",
    img: "/marketing/blueprint.png",
    label: "codexstudio · story blueprint",
    steps: [
      "Write a logline — your story's north star — then flesh out each act's pitch.",
      "Add chapter cards under any act, set a status (Planned / Drafting / Done) and slide the tension marker.",
      "Drag chapters between acts to reshape your structure, or switch to the outline view.",
      "Stuck? Hit AI: Expand to turn a logline into acts, or an act into chapters.",
    ],
  },
  {
    id: "beats",
    icon: ListChecks,
    title: "3. Detail-plan chapters with beats",
    intro: "Before you write a scene, sketch it out. The Beat / Scene Planner lets you list a chapter's beats as a simple, tickable checklist — with your logline, act and summary right there for context.",
    img: "/marketing/beats.png",
    label: "codexstudio · beat planner",
    steps: [
      "Open Beats from the manuscript toolbar on any chapter.",
      "Add beats in the order they'll happen, and tick each one off as you draft it.",
      "Reorder or remove beats as the scene takes shape.",
      "Short on ideas? Let 'AI: Suggest beats' draft a plan from your logline and chapter summary.",
    ],
  },
  {
    id: "writing",
    icon: PenSquare,
    title: "4. Write & auto-highlight",
    intro: "The Manuscript view is a focused, Word-class editor. As you write, CodexStudio recognises the details that matter.",
    img: "/marketing/manuscript.png",
    label: "codexstudio · manuscript",
    steps: [
      "Draft in the centre canvas — everything autosaves as you type.",
      "Chapters number themselves automatically (Chapter 1, 2, 3…) and renumber if you add, reorder or delete one — any custom titles like 'Prologue' are kept and skipped in the count.",
      "Flip on Highlight details to see characters, weapons, places and objects colour-coded in your prose.",
      "Click any highlight to edit that detail — the update reflects across every chapter instantly.",
      "Use the toolbar for headings, styles, lists and links, plus native spellcheck.",
    ],
  },
  {
    id: "codex",
    icon: Library,
    title: "5. The Storyboard Codex",
    intro: "The Codex is your story's living memory — a searchable board of every vital detail.",
    img: "/marketing/codex.png",
    label: "codexstudio · storyboard codex",
    steps: [
      "Browse detail cards grouped by type: characters, weapons, locations, objects, factions and more.",
      "Search or filter by type to find anything in seconds.",
      "Toggle Included to mark which details have made it into your draft.",
      "Add your own custom detail types with their own colours — and mark a type as a Kit (like a Med Kit or grab-bag) to give it a contents list.",
    ],
  },
  {
    id: "sections",
    icon: BookMarked,
    title: "6. Reusable sections & your Author Bio",
    intro: "Front and back matter you reuse in every book — Prologue, Dedication, About the Author, Acknowledgements, Epilogue — a click away, and never counted in your chapter numbering.",
    steps: [
      "Open the “+” menu in the Chapters list and pick a section — it drops in correctly named and placed at the front or back of your book.",
      "Write your Author Bio once and hit ‘Save as my bio’ — the About the Author section auto-fills with it in every book.",
      "‘Manage sections’ lets you add, rename or remove sections and set front/back placement — per book, or ‘Save as my default’ for future books.",
      "Sections are ordinary chapters once added, so you can move them anytime with the up/down arrows or by dragging on the Blueprint.",
    ],
  },
  {
    id: "ai-scan",
    icon: Sparkles,
    title: "7. AI Detail Scan & sensitivity",
    intro: "Let the AI read a chapter and surface the details worth tracking — with you in control of how much it captures.",
    steps: [
      "Open AI Scan from the studio toolbar to sweep the current chapter.",
      "Choose a sensitivity: Key details only, Balanced, or Everything.",
      "Prefer to review first? Switch to 'Review first' and tick exactly which details enter your Codex.",
      "Run Proofread anytime for a spelling and grammar pass.",
    ],
  },
  {
    id: "loadouts",
    icon: Boxes,
    title: "8. Loadouts & Gear Sync",
    intro: "For action, survival, fantasy and historical fiction, the loadout engine tracks every item, arrow, magazine and ration your characters carry.",
    img: "/marketing/loadouts.png",
    label: "codexstudio · loadouts",
    steps: [
      "Add owners (characters, vehicles or buildings) and give them gear, ammo, food and water.",
      "Ammo is any consumable type — 5.56mm rounds, shotgun shells, arrows or musket balls — and each weapon can carry its own calibre and magazine size (create a weapon in the Codex once and it's remembered everywhere).",
      "Mark a store like an armoury or quiver as a stock source so kit drawn from it is deducted automatically.",
      "Build a Kit in your Codex (say a Med Kit with 6 bandages and 2 tourniquets) — when a character picks it up, its contents auto-fill their loadout as a tick-off checklist.",
      "Type a plain-English note — 'fired 30 rounds', 'loosed a dozen arrows', 'down to two mags' (an absolute ammo-check) or 'restocked the med kit' — and let it update the counts and kit checklists.",
      "When a character is killed, a Gear Sync scan suggests marking them KIA (you confirm) — dead characters are frozen from gaining gear, but you can loot their kit onto the living and Redistribute ammo across the squad.",
      "Use Sync gear on a chapter to scan the scene, review each change, and watch for low-stock and continuity warnings.",
    ],
  },
  {
    id: "devices",
    icon: ShieldCheck,
    title: "9. Write across devices & windows",
    intro: "CodexStudio keeps your work safe whether you write on one screen or several — and can pop out onto a second monitor.",
    steps: [
      "Your manuscript is protected by auto-refresh, a save-guard that blocks stale overwrites, and a Version History you can restore from.",
      "Tick ‘Keep me signed in’ at login to stay signed in; untick it on a shared device to sign out when the browser closes.",
      "On the Codex, Loadouts and Blueprint, hit ‘Pop out’ to open that panel in its own resizable window — ideal for a second monitor.",
      "Edits sync live across every open window in about a second, and each pop-out remembers its size.",
      "Install CodexStudio as an app from your browser to add it to your home screen or desktop.",
    ],
  },
  {
    id: "branching",
    icon: Waypoints,
    title: "10. Branching & CYOA",
    intro: "Writing a choose-your-own-adventure? Switch a project to Branching mode and build interactive paths.",
    steps: [
      "Toggle Linear ↔ Branching from the Blueprint header (your data is kept either way).",
      "Give any section choices, each linking to an existing or brand-new section.",
      "Open the visual Map to see your whole story as a node-and-arrow graph, with Start, endings and orphans flagged.",
    ],
  },
  ...(AUDIOBOOKS_ENABLED ? [{
    id: "narration",
    icon: Headphones,
    title: "11. AI Narration & audiobooks",
    intro: "Turn your writing into natural, studio-quality audio — a chapter at a time, or the whole book.",
    steps: [
      "Open a chapter and click Narrate. Pick one of six narrators (British & US voices) and hit Preview to hear a free sample before you spend anything.",
      "You'll see the exact credit cost up front — it's based on the chapter's size, so short chapters cost little and long ones cost more. Finalise your chapter first: editing and re-generating costs again.",
      "Give any Codex detail a 'Say it like' pronunciation so invented names and places are read correctly, every time.",
      "Flip on Premium quality for warmer, more expressive narration on your final release (it uses 2× credits).",
      "Once generated, audio is cached — it plays back instantly and free, and you can download the MP3 (named after your book & chapter).",
      "Use Tools → Full audiobook to stitch every narrated chapter into one file, with spoken chapter titles. Missing or edited chapters are flagged, and one button generates them all.",
    ],
  }] : []),
  {
    id: "export",
    icon: Download,
    title: "12. Save, sync & export",
    intro: "Your work is always yours — and always backed up.",
    steps: [
      "Everything autosaves — you'll see 'All changes saved' in the header, and past versions are kept so you can roll back.",
      "Export a clean manuscript to PDF or Word (.docx), or take your codex with you as data.",
      "Upload cover art to each project to make your desk feel like a real bookshelf.",
    ],
  },
];

export default function Guide() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <MarketingNav>
        <Link to="/" className="hover:text-foreground transition-colors" data-testid="guide-nav-home">Home</Link>
        <button onClick={() => scrollTo("getting-started")} className="hover:text-foreground transition-colors" data-testid="guide-nav-start">Get started</button>
      </MarketingNav>

      {/* Header */}
      <section className="pt-32 pb-12 sm:pt-40 sm:pb-16 border-b border-border">
        <div className="max-w-5xl mx-auto px-5 sm:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <span className="text-xs uppercase tracking-widest font-semibold text-primary">The Guide</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mt-3">
              How CodexStudio works
            </h1>
            <p className="font-manuscript text-lg text-muted-foreground max-w-2xl mt-5 leading-relaxed">
              A quick tour of everything from your first chapter to a finished, continuity-perfect draft.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 grid lg:grid-cols-[220px_1fr] gap-10 lg:gap-16">
        {/* TOC */}
        <aside className="hidden lg:block">
          <nav className="sticky top-24 space-y-1" data-testid="guide-toc">
            <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-3">Contents</p>
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => scrollTo(s.id)}
                data-testid={`toc-${s.id}`}
                className="flex items-center gap-2.5 w-full text-left text-sm text-muted-foreground hover:text-primary py-1.5 transition-colors"
              >
                <s.icon className="w-4 h-4 shrink-0" /> <span className="truncate">{s.title}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Body */}
        <div className="space-y-16 sm:space-y-24 min-w-0">
          {SECTIONS.map((s) => (
            <motion.section
              key={s.id}
              id={s.id}
              data-testid={`guide-section-${s.id}`}
              className="scroll-mt-24"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-primary/12 grid place-items-center shrink-0">
                  <s.icon className="w-5 h-5 text-primary" />
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{s.title}</h2>
              </div>
              <p className="text-muted-foreground text-base sm:text-lg leading-relaxed mb-6">{s.intro}</p>
              {s.img && (
                <ScreenshotFrame testid={`guide-shot-${s.id}`} src={s.img} alt={s.title} label={s.label} className="mb-6" />
              )}
              <ol className="space-y-3">
                {s.steps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3.5">
                    <span className="mt-0.5 w-6 h-6 rounded-full bg-primary/15 text-primary text-xs font-bold grid place-items-center shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-foreground/90 text-sm sm:text-base leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </motion.section>
          ))}

          {/* CTA */}
          <div className="rounded-2xl border border-border bg-secondary/30 p-8 sm:p-10 text-center">
            <h3 className="font-display text-3xl font-bold mb-2">Ready to start writing?</h3>
            <p className="text-muted-foreground mb-6">Your first project takes less than a minute to create.</p>
            <Button data-testid="guide-cta" size="lg" onClick={() => navigate("/auth")} className="gap-2 font-semibold">
              Create your free account <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <MarketingFooter />
    </div>
  );
}
